let canvasInstance: HTMLCanvasElement | null = null;
let contextInstance: CanvasRenderingContext2D | null = null;

// Text measurement cache
const CacheSize = 400;
const textMeasurementCache = new Map();

const getCacheKey = (text: string, font: string, letterSpacing: string) => {
  return `${text}::${font}::${letterSpacing}`;
};

export const getCanvasContext = () => {
  if (!canvasInstance) {
    canvasInstance = document.createElement('canvas');
    contextInstance = canvasInstance.getContext('2d');
  }
  return contextInstance;
};

export const measureText = (text: string, font: string, letterSpacing: string) => {
  const cacheKey = getCacheKey(text, font, letterSpacing);

  if (textMeasurementCache.has(cacheKey)) {
    return textMeasurementCache.get(cacheKey);
  }

  const context = getCanvasContext();
  if (!context) {
    return 0;
  }

  context.font = font;
  if ('letterSpacing' in context) {
    context.letterSpacing = letterSpacing;
  }

  const width = context.measureText(text).width;

  if (textMeasurementCache.size > CacheSize) {
    const oldestKey = textMeasurementCache.keys().next().value;
    textMeasurementCache.delete(oldestKey);
  }

  textMeasurementCache.set(cacheKey, width);
  return width;
};

export const clearTextMeasurementCache = () => {
  if (canvasInstance) {
    contextInstance = null;
    canvasInstance = null;
    textMeasurementCache.clear();
  }
};

const Ellipsis = '…';

/**
 * Truncates text to fit within a maximum pixel width, adding ellipsis if truncated.
 * Uses binary search for efficiency.
 */
export const truncateToWidth = (
  text: string,
  maxWidth: number,
  font: string,
  letterSpacing: string,
): string => {
  const fullWidth = measureText(text, font, letterSpacing);
  if (fullWidth <= maxWidth) {
    return text;
  }

  const ellipsisWidth = measureText(Ellipsis, font, letterSpacing);
  const availableWidth = maxWidth - ellipsisWidth;

  if (availableWidth <= 0) {
    return Ellipsis;
  }

  // Binary search for the optimal truncation point
  let low = 0;
  let high = text.length;
  let bestLength = 0;

  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    const truncated = text.slice(0, mid);
    const width = measureText(truncated, font, letterSpacing);

    if (width <= availableWidth) {
      bestLength = mid;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }

  if (bestLength === 0) {
    return Ellipsis;
  }

  return text.slice(0, bestLength) + Ellipsis;
};
